/**
 * Backorder Pipeline Component - MaintainX Style
 * Drag-and-drop pipeline view for backorder management
 * Reusable across Admin, Hospital, and Supplier dashboards
 */

import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Package,
  Send,
  FileText,
  DollarSign,
  CheckCircle,
  Truck,
  Clock,
  X,
  Eye,
  GripVertical,
  Loader2,
  RefreshCw,
  Building2,
  User,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Mail,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import BackorderDetailView from "@/components/BackorderDetailView";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pipeline stages for backorders
const BACKORDER_STAGES = [
  { key: "submitted", label: "Submitted", icon: FileText, color: "bg-blue-500" },
  { key: "sent_to_suppliers", label: "Sent to Suppliers", icon: Send, color: "bg-purple-500" },
  { key: "quotes_received", label: "Quotes Received", icon: DollarSign, color: "bg-amber-500" },
  { key: "approved", label: "Approved", icon: CheckCircle, color: "bg-emerald-500" },
  { key: "ordered", label: "Ordered", icon: Package, color: "bg-indigo-500" },
  { key: "shipped", label: "Shipped", icon: Truck, color: "bg-teal-500" },
  { key: "delivered", label: "Delivered", icon: CheckCircle, color: "bg-green-600" },
  { key: "cancelled", label: "Cancelled", icon: X, color: "bg-red-500" }
];

// Draggable Card Component
function DraggableCard({ item, children, canDrag = true }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    disabled: !canDrag
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow ${
        isDragging ? "ring-2 ring-blue-500" : ""
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        {canDrag && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 mt-1"
          >
            <GripVertical size={16} />
          </div>
        )}
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({ stage, items, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  const Icon = stage.icon;

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[280px] max-w-[320px] rounded-xl border transition-colors ${
        isOver ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-50"
      }`}
    >
      {/* Column Header */}
      <div className={`px-4 py-3 rounded-t-xl ${stage.color}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Icon size={18} />
            <span className="font-semibold text-sm">{stage.label}</span>
          </div>
          <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {items.length}
          </span>
        </div>
      </div>

      {/* Column Body */}
      <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-[calc(100vh-300px)]">
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        
        {items.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            No items
          </div>
        )}
      </div>
    </div>
  );
}

// Backorder Card Content
const BackorderCard = ({ item, onView, onStatusChange, onDelete, canDelete }) => {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between">
        <h4 className="font-medium text-slate-900 text-sm line-clamp-2">
          {item.product_name || item.description || "Backorder Request"}
        </h4>
      </div>
      
      <div className="space-y-1 text-xs text-slate-500">
        {item.hospital_name && (
          <div className="flex items-center gap-1.5">
            <Building2 size={12} />
            <span className="truncate">{item.hospital_name}</span>
          </div>
        )}
        {item.requester_name && (
          <div className="flex items-center gap-1.5">
            <User size={12} />
            <span className="truncate">{item.requester_name}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Calendar size={12} />
          <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : "Recently"}</span>
        </div>
        {item.quantity && (
          <div className="flex items-center gap-1.5">
            <Package size={12} />
            <span>Qty: {item.quantity}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-xs font-mono text-slate-400">
          #{item.id?.slice(-6) || "N/A"}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-blue-600 hover:text-blue-700"
            onClick={() => onView(item)}
          >
            <Eye size={12} className="mr-1" />
            View
          </Button>
          {canDelete && onDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-400 hover:text-rose-600 hover:bg-rose-50 px-1.5"
              onClick={(e) => { e.stopPropagation(); onDelete(item); }}
              title="Delete request"
              data-testid={`bo-card-delete-${item.id}`}
            >
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Detail Modal Component
const BackorderDetailModal = ({ item, open, onClose, onStatusChange, onSendNotification }) => {
  const [newStatus, setNewStatus] = useState(item?.status || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  if (!item) return null;

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === item.status) return;
    
    setLoading(true);
    try {
      await onStatusChange(item.id, newStatus, notes);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Backorder Details</DialogTitle>
          <DialogDescription>
            Request #{item.id?.slice(-8) || "N/A"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">
              {item.product_name || item.description || "Product Request"}
            </h4>
            {item.quantity && (
              <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
            )}
            {item.manufacturer && (
              <p className="text-sm text-slate-600">Manufacturer: {item.manufacturer}</p>
            )}
          </div>

          {/* Hospital & Requester */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500">Hospital</label>
              <p className="font-medium text-slate-900">{item.hospital_name || "N/A"}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">Requested By</label>
              <p className="font-medium text-slate-900">{item.requester_name || "N/A"}</p>
            </div>
          </div>

          {/* Current Status */}
          <div>
            <label className="text-xs text-slate-500 mb-2 block">Current Status</label>
            <div className="flex items-center gap-2">
              {BACKORDER_STAGES.map(stage => (
                <button
                  key={stage.key}
                  onClick={() => setNewStatus(stage.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    (newStatus || item.status) === stage.key
                      ? `${stage.color} text-white`
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-slate-500 mb-2 block">Update Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this status update..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onSendNotification(item, "email")}>
            <Mail size={16} className="mr-2" />
            Email Update
          </Button>
          <Button variant="outline" onClick={() => onSendNotification(item, "telegram")}>
            <MessageSquare size={16} className="mr-2" />
            Telegram
          </Button>
          <Button 
            onClick={handleStatusUpdate} 
            disabled={loading || newStatus === item.status}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
            Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Pipeline Component
export default function BackorderPipeline({ 
  viewMode = "admin", // "admin", "hospital", "supplier"
  hospitalId = null,
  supplierId = null,
  onRefresh = null,
  onStatusChange = null, // Callback when status changes for bi-directional sync
  externalItems = null, // Controlled items from parent for bi-directional sync
  authToken = null // Optional - for hospital/supplier portals that use different auth
}) {
  const { token: contextToken, user } = useAuth();
  const token = authToken || contextToken; // Use passed token if available
  const [internalItems, setInternalItems] = useState([]);
  // When using external items (controlled mode), don't start with loading=true
  const [loading, setLoading] = useState(!externalItems);
  const [activeItem, setActiveItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Use external items if provided (bi-directional sync mode)
  const items = externalItems || internalItems;
  const setItems = externalItems ? () => {} : setInternalItems; // No-op if controlled

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // Fetch backorders (only if not in controlled mode)
  const fetchBackorders = useCallback(async () => {
    if (!token || externalItems) return; // Skip if using external items
    
    setLoading(true);
    try {
      let endpoint;
      
      // Use different endpoints based on view mode
      if (viewMode === "hospital") {
        // Hospital portal uses the authenticated hospitals endpoint
        endpoint = `${API_URL}/api/hospitals/requests`;
      } else if (viewMode === "supplier" && supplierId) {
        // Supplier view
        endpoint = `${API_URL}/api/backorders/requests?supplier_id=${supplierId}`;
      } else {
        // Admin view - uses the main backorders endpoint
        endpoint = `${API_URL}/api/backorders/requests`;
      }
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Ensure each item has an id - data comes as { requests: [...] } or { backorders: [...] }
        const backorders = (data.requests || data.backorders || data || []).map(item => ({
          ...item,
          id: item.id || item._id || item.request_id || Math.random().toString(36)
        }));
        setInternalItems(backorders);
      }
    } catch (error) {
      console.error("Failed to fetch backorders:", error);
      toast.error("Failed to load backorders");
    } finally {
      setLoading(false);
    }
  }, [token, viewMode, hospitalId, supplierId]);

  useEffect(() => {
    fetchBackorders();
  }, [fetchBackorders]);

  // Handle status change
  const handleStatusChange = async (itemId, newStatus, notes = "") => {
    try {
      const response = await fetch(`${API_URL}/api/backorders/${itemId}/status`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus, notes })
      });

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        
        // Update local state (only if not in controlled mode)
        if (!externalItems) {
          setInternalItems(prev => prev.map(item => 
            item.id === itemId ? { ...item, status: newStatus } : item
          ));
        }
        
        // Call onStatusChange for bi-directional sync
        onStatusChange?.(itemId, newStatus, notes);
        
        // Trigger refresh callback if provided
        onRefresh?.();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Update failed");
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over) return;

    const itemId = active.id;
    const newStatus = over.id;
    
    // Find the item
    const item = items.find(i => i.id === itemId);
    if (!item || item.status === newStatus) return;

    // Check if this is a valid status transition
    const currentIndex = BACKORDER_STAGES.findIndex(s => s.key === item.status);
    const newIndex = BACKORDER_STAGES.findIndex(s => s.key === newStatus);
    
    // Allow forward movement or cancellation
    if (newIndex > currentIndex || newStatus === "cancelled") {
      handleStatusChange(itemId, newStatus);
    } else {
      toast.error("Cannot move backward in pipeline");
    }
  };

  const handleDragStart = (event) => {
    const item = items.find(i => i.id === event.active.id);
    setActiveItem(item);
  };

  // Handle notification
  const handleSendNotification = async (item, type) => {
    try {
      const endpoint = type === "email" 
        ? `${API_URL}/api/backorders/${item.id}/notify-email`
        : `${API_URL}/api/backorders/${item.id}/notify-telegram`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        toast.success(`${type === "email" ? "Email" : "Telegram"} notification sent!`);
      } else {
        toast.error(`Failed to send ${type} notification`);
      }
    } catch (error) {
      toast.error("Notification failed");
    }
  };

  // Group items by status
  const itemsByStage = BACKORDER_STAGES.reduce((acc, stage) => {
    acc[stage.key] = items.filter(item => item.status === stage.key);
    return acc;
  }, {});

  // Determine if user can drag (admin only for full control)
  const canDrag = viewMode === "admin" || user?.role === "super_admin";

  // Delete a backorder. Admin uses /api/admin/backorders/{id} (any record);
  // Hospital uses /api/hospital/my-backorders/{id} (own-records only, gated by
  // backend ownership + status="new" check).
  const isAdminView = viewMode === "admin" || user?.role === "super_admin" || user?.role === "admin";
  const canDelete = isAdminView || viewMode === "hospital";
  const handleDelete = async (item) => {
    const id = item?.id;
    if (!id) return;
    const yes = window.confirm(
      `Delete backorder request ${id}?\n\nThis cannot be undone.${isAdminView ? " Any attached supplier quotes will also be removed." : ""}`,
    );
    if (!yes) return;
    const endpoint = isAdminView
      ? `${API_URL}/api/admin/backorders/${id}`
      : `${API_URL}/api/hospital/my-backorders/${id}`;
    try {
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Backorder deleted");
      setItems((prev) => prev.filter((it) => it.id !== id));
      fetchBackorders();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Horizontal scroll for pipeline
  const pipelineRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const checkScrollButtons = () => {
    if (pipelineRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = pipelineRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    const container = pipelineRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [items]);

  const scrollPipeline = (direction) => {
    if (pipelineRef.current) {
      const scrollAmount = 320; // About one column width
      pipelineRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          Backorder Pipeline
          <span className="ml-2 text-sm font-normal text-slate-500">
            ({items.length} total)
          </span>
        </h3>
        <Button variant="outline" size="sm" onClick={fetchBackorders}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pipeline with Scroll Arrows */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="relative">
          {/* Left Scroll Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scrollPipeline('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center border border-slate-200 text-slate-600 hover:text-slate-900 transition-all hover:scale-110"
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Right Scroll Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scrollPipeline('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center border border-slate-200 text-slate-600 hover:text-slate-900 transition-all hover:scale-110"
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* Pipeline Container */}
          <div 
            ref={pipelineRef}
            className="flex gap-4 overflow-x-auto pb-4 px-12 scroll-smooth"
            style={{ scrollbarWidth: 'thin' }}
          >
            {BACKORDER_STAGES.map(stage => (
              <DroppableColumn
                key={stage.key}
                stage={stage}
                items={itemsByStage[stage.key] || []}
              >
                {(itemsByStage[stage.key] || []).map(item => (
                  <DraggableCard key={item.id} item={item} canDrag={canDrag}>
                    <BackorderCard
                      item={item}
                      onView={(item) => {
                        setDetailItem(item);
                        setShowDetailModal(true);
                      }}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                      canDelete={canDelete}
                    />
                  </DraggableCard>
                ))}
              </DroppableColumn>
            ))}
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem && (
            <div className="bg-white rounded-lg border-2 border-blue-500 shadow-lg p-3 w-72">
              <BackorderCard
                item={activeItem}
                onView={() => {}}
                onStatusChange={() => {}}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package size={28} className="text-slate-400" />
          </div>
          <h4 className="text-lg font-semibold text-slate-900 mb-2">No backorders yet</h4>
          <p className="text-slate-500">Backorder requests will appear here</p>
        </div>
      )}

      {/* Detail Modal - Unified view with quotes */}
      <BackorderDetailView
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setDetailItem(null);
        }}
        backorder={detailItem}
        viewMode={viewMode}
        token={token}
        onRefresh={() => {
          fetchBackorders();
          if (onRefresh) onRefresh();
        }}
      />
    </div>
  );
}

// Export stage config for use elsewhere
export { BACKORDER_STAGES };
